import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { useLoading } from '@sa/hooks';
import { fetchGetUserInfo, fetchLogin, fetchLogout, fetchWxCpLogin } from '@/service/api';
import { useRouterPush } from '@/hooks/common/router';
import { localStg } from '@/utils/storage';
import { SetupStoreId } from '@/enum';
import { $t } from '@/locales';
import type { Api } from '@/typings/api';
import { useRouteStore } from '../route';
import { useTabStore } from '../tab';
import { clearAuthStorage, getToken, getUserInfo } from './shared';

export const useAuthStore = defineStore(SetupStoreId.Auth, () => {
  const route = useRoute();
  const routeStore = useRouteStore();
  const tagStore = useTabStore();
  const { toLogin, redirectFromLogin } = useRouterPush(false);
  const { loading: loginLoading, startLoading, endLoading } = useLoading();

  const token = ref(getToken());

  const userInfo: Api.Auth.UserInfo = reactive(getUserInfo());

  const permissions: string[] = reactive([]);
  fetchUserInfo();

  /** is super role in static route */
  const isStaticSuper = computed(() => {
    const { VITE_AUTH_ROUTE_MODE, VITE_STATIC_SUPER_ROLE } = import.meta.env;

    return VITE_AUTH_ROUTE_MODE === 'static' && userInfo.roles.includes(VITE_STATIC_SUPER_ROLE);
  });

  /** Is login */
  const isLogin = computed(() => Boolean(token.value));

  /** Reset auth store */
  async function resetStore() {
    const authStore = useAuthStore();
    await fetchLogout();
    clearAuthStorage();

    authStore.$reset();

    if (!route.meta.constant) {
      await toLogin();
    }

    routeStore.resetStore();
    tagStore.clearTabs();
  }

  /**
   * Login
   *
   * @param userName User name
   * @param password Password
   * @param rememberMe
   * @param customer 企业ID
   */
  // eslint-disable-next-line max-params
  async function login(userName: string, password: string, rememberMe: boolean, customer?: string) {
    startLoading();

    const { data: info, error } = await fetchLogin(userName, password, rememberMe, customer);

    if (!error) {
      const pass = await loginByToken(info);
      if (pass) {
        await routeStore.initAuthRoute();

        await redirectFromLogin();

        if (routeStore.isInitAuthRoute) {
          window.$notification?.success({
            title: $t('page.login.common.loginSuccess'),
            content: $t('page.login.common.welcomeBack', { userName: userInfo.userName }),
            duration: 4500
          });
        }
      }
    } else {
      resetStore();
    }

    endLoading();
  }
  /**
   * 企业微信登录
   *
   * @param code Code
   */
  async function loginByWxCpCode(code: string) {
    startLoading();

    const { data: info, error } = await fetchWxCpLogin(code);

    if (!error) {
      const pass = await loginByToken(info);
      if (pass) {
        await routeStore.initAuthRoute();

        await redirectFromLogin();

        if (routeStore.isInitAuthRoute) {
          window.$notification?.success({
            title: $t('page.login.common.loginSuccess'),
            content: $t('page.login.common.welcomeBack', { userName: userInfo.userName }),
            duration: 4500
          });
        }
      }
    } else {
      resetStore();
    }

    endLoading();
  }

  async function fetchUserInfo() {
    const { data } = await fetchGetUserInfo();
    if (data) {
      loginByToken({ ...data, token: token.value });
    }
  }

  async function loginByToken(info: Api.Auth.UserInfo) {
    // 1. stored in the localStorage, the later requests need it in headers
    localStg.set('token', info.token);

    // if (!error) {
    // 2. store user info
    localStg.set('userInfo', info);
    // 3. update auth route
    token.value = info.token;
    Object.assign(userInfo, info);

    return true;
    // }

    // return false;
  }

  return {
    token,
    userInfo,
    permissions,
    isStaticSuper,
    isLogin,
    loginLoading,
    resetStore,
    login,
    loginByWxCpCode
  };
});
